"use client";

import React, { useState, useEffect, useCallback } from "react";
import { motion } from 'framer-motion';
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import CustomerApiService from "@/services/CustomerApiService";
import { useCustomerPanelTranslations } from "@/lib/customer-panel-utils";
import { extractErrorMessage } from "@/lib/customer-panel/error-handler";
import { ErrorMessage, SuccessMessage, LoadingSpinner } from "@/lib/customer-panel/ui-components";
import { fadeInVariants, cardVariants, buttonVariants } from "@/lib/animations";
import type { PageUrlMap } from "@/types/blog";

interface CustomerNewsletterProps {
  language?: string;
  languageId?: number;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  __grapesPreview?: boolean;
}

export default function CustomerNewsletter({
  language = 'en',
  languageId,
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  __grapesPreview = false,
  ...restProps
}: CustomerNewsletterProps) {
  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/CustomerNewsletter',
    { language, translations, pageUrls },
    isBackendRender
  );

  const t = useCustomerPanelTranslations(translations);
  const [subscribed, setSubscribed] = useState<boolean>(false);
  const [subscribedAt, setSubscribedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load newsletter status
  useEffect(() => {
    if (isBackendRender) {
      // Backend render modunda mock data
      setSubscribed(true);
      setSubscribedAt(new Date().toISOString());
      setLoading(false);
      return;
    }
    
    const loadStatus = async () => {
      try {
        const status = await CustomerApiService.getNewsletterStatus();
        setSubscribed(status.subscribed);
        setSubscribedAt(status.subscribed_at || null);
      } catch (err) {
        setError(extractErrorMessage(err, t('customer_panel.common.error', 'An error occurred')));
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
  }, [t, isBackendRender]);

  const handleSubscribe = async () => {
    if (isBackendRender) return;
    
    setActionLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await CustomerApiService.subscribeNewsletter();
      setSubscribed(true);
      setSubscribedAt(new Date().toISOString());
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(extractErrorMessage(err, t('customer_panel.common.error', 'An error occurred')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (isBackendRender) return;
    
    if (!confirm(t('customer_panel.newsletter.unsubscribe', 'Unsubscribe'))) {
      return;
    }

    setActionLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await CustomerApiService.unsubscribeNewsletter();
      setSubscribed(false);
      setSubscribedAt(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(extractErrorMessage(err, t('customer_panel.common.error', 'An error occurred')));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div {...backendAttributes} className="container mx-auto px-4 py-8" role="status" aria-live="polite">
        <LoadingSpinner size="lg" label={t('customer_panel.common.loading', 'Loading...')} />
      </div>
    );
  }

  return (
    <motion.div 
      {...backendAttributes}
      variants={fadeInVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div 
        className="mb-8"
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
      >
        <h1 className="text-3xl font-bold text-gray-900">
          {t('customer_panel.newsletter.title', 'Newsletter Management')}
        </h1>
      </motion.div>

      {/* Content */}
      <motion.div 
        className="bg-white rounded-lg drop-shadow-card border border-gray-200 p-6 group hover:shadow-2xl transition-all duration-300"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
                {error && <ErrorMessage message={error} className="mb-6" />}
                {success && <SuccessMessage message={t('customer_panel.common.update_success', 'Successfully updated')} className="mb-6" />}

        <div className="space-y-6">
          {/* Status */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('customer_panel.newsletter.subscription_status', 'Subscription Status')}
            </h2>
            <div className="p-4 bg-gray-50 rounded-lg">
              {subscribed ? (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {t('customer_panel.newsletter.subscribed', 'You are subscribed to our newsletter.')}
                  </p>
                  {subscribedAt && (
                    <p className="text-xs text-gray-500">
                      {t('customer_panel.newsletter.subscription_date', 'Subscription Date')}:{' '}
                      {new Date(subscribedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm font-medium text-gray-700">
                  {t('customer_panel.newsletter.not_subscribed', 'You are not subscribed to our newsletter.')}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end">
            {subscribed ? (
              <motion.button
                type="button"
                onClick={handleUnsubscribe}
                disabled={actionLoading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={actionLoading ? t('customer_panel.common.loading', 'Loading...') : t('customer_panel.newsletter.unsubscribe', 'Unsubscribe')}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                {actionLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="inline-block mr-2" />
                    {t('customer_panel.common.loading', 'Loading...')}
                  </>
                ) : (
                  t('customer_panel.newsletter.unsubscribe', 'Unsubscribe')
                )}
              </motion.button>
            ) : (
              <motion.button
                type="button"
                onClick={handleSubscribe}
                disabled={actionLoading}
                className="px-6 py-2 bg-alsero-first text-white rounded-md hover:bg-alsero-second focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={actionLoading ? t('customer_panel.common.loading', 'Loading...') : t('customer_panel.newsletter.subscribe', 'Subscribe')}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                {actionLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="inline-block mr-2" />
                    {t('customer_panel.common.loading', 'Loading...')}
                  </>
                ) : (
                  t('customer_panel.newsletter.subscribe', 'Subscribe')
                )}
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

